import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { File } from '@klickdata/core/file';
import { MessageDeletedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, ResourceTag, ResourceTypes } from '@klickdata/core/resource';
import { GlobalFilterProperty, SelectFilterOption } from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { MatTableBase } from 'apps/klickdata/src/app/shared/table/mat-table-base';
import { filter, switchMap, takeUntil, tap } from 'rxjs';
import { K3VideosListingService } from '../k3-videos-listing.service';
import { DialogVideoPlayerComponent } from '../../dialog-video-player/dialog-video-player.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-videos-listing-general',
    templateUrl: './videos-listing-general.component.html',
    styleUrls: ['./videos-listing-general.component.scss'],
    providers: [K3VideosListingService],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class VideosListingGeneralComponent extends MatTableBase<File> implements AfterViewInit {
    @Input() public pageSize = 25;
    @Input() public columns = [
        'id',
        'title',
        'description',
        'tags',
        'visibility',
        'priority',
        'author',
        'updated_at',
        'favorite',
        'tools',
    ];
    @Input() visibility = 'private';
    AppScope = AppScope;
    public expandedRow: File | null;
    public filePrioOptions: SelectFilterOption[];
    GlobalFilterProperty = GlobalFilterProperty;
    public significantPrio = ['a1', 'a2', 'a3', 'b1', 'c3'];
    public filterFavorite = false;
    public authUser: User;
    constructor(
        protected auth: AuthService,
        protected listService: K3VideosListingService,
        protected mobileService: MobileService,
        protected cdr: ChangeDetectorRef,
        protected message: MessageService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected downloadHelper: DownloadHelperService,
        protected dialog: MatDialog
    ) {
        super(cdr, mobileService, listService);
        this.filePrioOptions = Utils.getTaskPrioOptions();
        this.auth
            .getUser()
            .pipe(takeUntil(this.destroy))
            .subscribe((user) => {
                this.authUser = user;
            });
    }
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.route.queryParams.pipe(filter((params) => params.action === 'addVideo')).subscribe(() =>
            setTimeout(() => {
                this.addVideo();
                this.router.navigate([this.router.url.split('?')[0]]);
            }, 500)
        );
        this.listenToNewFile();
    }
    public onFilterFavoriteClick(): void {
        if (this.filterFavorite) {
            this.filter.createOrUpdateFilter([{ property: 'favorite', items: [this.authUser.id] }]);
        } else {
            this.filter.ignoreFilter(['favorite']);
        }
    }
    public favoriteResource(file: File) {
        this.listService
            .update({ id: file.id, favorite: !file.favorite }, 'user_feeds')
            .pipe(takeUntil(this.destroy))
            .subscribe((result) => {
                file.favorite = result.favorite;
                this.cdr.markForCheck();
            });
    }
    public listenToNewFile() {
        this.listService
            .listenToNewFile()
            .pipe(takeUntil(this.destroy))
            .subscribe((file) => this.dataSource.replace(file));
    }

    public addVideo() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.AI_CREATE_UPDATE_MEDIA,
            data: {
                type: 'addVideo',
                visibility: this.visibility,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                tap(() => this.router.navigate([], { queryParams: null })),
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'addVideo'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data.visibility == this.visibility) {
                    this.dataSource.add(new File(data.value));
                }
                this.cdr.markForCheck();
            });
    }
    public view(file: File) {
        this.dialog.open(DialogVideoPlayerComponent, {
            maxWidth: '60%',
            data: {
                id: file.medias.file[0],
                autoplay: false,
            },
        });
    }

    onTagClick(tag: ResourceTag) {
        this.filter.filterByModel(tag.getFilter(), true);
    }

    updateFileTags(file: File, tagsIds: number[]) {
        this.listService
            .update({ id: file.id, tag_ids: tagsIds })
            .pipe(
                takeUntil(this.destroy),
                tap(() => this.dataSource.refresh())
            )
            .subscribe();
    }
    onSelectPriority(file: File, prio: string) {
        this.listService
            .update({ id: file.id, priority: prio })
            .pipe(
                takeUntil(this.destroy),
                tap(() => this.dataSource.refresh())
            )
            .subscribe();
    }
    public download(file: File) {
        if (file.downloads) {
            this.downloadHelper.download(file.downloads);
        }
    }
    public delete(file: File) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete ${file.title}`,
                contentBody: $localize`You are about to delete ${file.title}. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap((res) => {
                    if (res) {
                        return this.listService.destroy(file);
                    }
                })
            )
            .subscribe((action) => {
                if (action) {
                    this.dataSource.removeById(file.id);
                    this.message.openMessage(MessageDeletedComponent);
                }
            });
    }
    public createMaterial(file: File) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.CREATE_RESOURCE,
            data: {
                typeId: ResourceTypes.DocMaterial,
                items: file.medias?.file?.map((mediaId) => ({ file_item_id: mediaId })),
            },
        });
    }
    public edit(file: File) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_FILE,
            data: {
                file: file,
                type: 'editFile',
                visibility: this.visibility,
            },
        });

        this.mobileService
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobileService.getSideNavResponseData()),
                filter((data) => data.type === 'editFile'),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data.visibility == this.visibility) {
                    this.dataSource.replace(new File(data.value));
                }
                this.cdr.markForCheck();
            });
    }
}
